import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { router } from '../platform/router.shim';
import { setBuildNavigate, useBuildRun } from '../state/buildRun';
import { useSession } from '../state/useSession';
import { watchBuildRun } from '../analytics/buildFunnel';
import { watchErrorsShown } from '../analytics/errorsShown';
import BuildOverlay from './BuildOverlay';
import ConsentBanner from './ConsentBanner';
import TabBar from './TabBar';
import { usePageViews } from '../analytics/visits';

/** Routes that own the whole viewport: the editor, the wizard and the auth screens. */
const FULL_BLEED = [/^\/project\//, /^\/new/, /^\/sign-/, /^\/confirm/, /^\/forgot-password/];

export default function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useSession();
  // Every route counted, first-party (analytics/visits.ts).
  usePageViews();

  /*
   * Hand React Router's navigate to the things that navigate from OUTSIDE React.
   *
   * The build store is the one that matters: a build finishes, the app is published and
   * the credits are spent, and then nothing happens because the store has nowhere to send
   * anyone. Shipped exactly that way — unit tests mock the store, so only a live run
   * caught it, and to the person it looked like a build that never finished.
   */
  useEffect(() => {
    router.setNavigate(navigate as never);
    setBuildNavigate((path, opts) => navigate(path, opts));
    return () => {
      router.setNavigate(null);
      setBuildNavigate(null);
    };
  }, [navigate]);

  /*
   * A build this browser started and never heard back from — the tab was discarded or
   * reloaded while the server worked — is said once, on whatever page the person lands on.
   * Its row sits in Build either way; this is what tells them to look, and its Try again
   * collects the finished build under the same key rather than paying for it again.
   */
  useEffect(() => {
    if (user?.id) useBuildRun.getState().recover(user.id);
  }, [user?.id]);

  // The funnel's build_started / build_succeeded / build_failed: a build ends on whatever page
  // the person is on, and the shell is on every one (analytics/buildFunnel.ts).
  useEffect(() => watchBuildRun(useBuildRun), []);
  // error_shown: every role="alert" the builder shows, by where and code, never its words.
  useEffect(() => watchErrorsShown(), []);

  const chrome = !FULL_BLEED.some((re) => re.test(pathname));
  return (
    <>
      <main className="app-column">{children}</main>
      {chrome && <TabBar />}
      {/* Outside <main> so a build survives every navigation. */}
      <BuildOverlay />
      <ConsentBanner />
    </>
  );
}
